import { useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ShoppingBag, UtensilsCrossed } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { EmptyState, Spinner } from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { useStorefront } from "@/lib/storefront";
import { useCart } from "@/lib/cart";
import { appConfigFromBootstrap } from "@/lib/api";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import type { ResponsiveImageSource } from "@/lib/types";
import {
  mainMenuDestination,
  onlineOrderDestination,
  orderingModeAvailability,
} from "@/lib/menu-navigation";
import dishImage from "@/assets/dish-grill.jpg";
import branchImage from "@/assets/branch-interior.jpg";

export const Route = createFileRoute("/main-menu")({
  component: MainMenu,
});

function MainMenu() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const bootstrap = useStorefront();
  const config = appConfigFromBootstrap(bootstrap);
  const { cart } = useCart();
  const { dineIn, online } = orderingModeAvailability(bootstrap);
  const destination = mainMenuDestination(bootstrap, cart.orderType?.type);
  const onlineDestination = onlineOrderDestination(
    bootstrap,
    cart.orderType?.type,
  );

  useEffect(() => {
    if (destination !== "/main-menu") {
      navigate({ to: destination, replace: true });
    }
  }, [destination, navigate]);

  if (destination !== "/main-menu") {
    return (
      <PageShell>
        <p
          className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground"
          role="status"
        >
          <Spinner /> {t("common.loading")}
        </p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="mb-10 text-center">
        <h1 className="text-3xl font-black sm:text-5xl">
          {t("mainMenu.title")}
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          {t("mainMenu.subtitle")}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {dineIn ? (
          <OptionCard
            to="/branchMenu"
            image={config.dineInMenuImage ?? branchImage}
            icon={<UtensilsCrossed className="size-6" />}
            title={t("mainMenu.dinein")}
            description={t("mainMenu.dineinDesc")}
          />
        ) : null}
        {online ? (
          <OptionCard
            to={onlineDestination}
            image={config.onlineMenuImage ?? dishImage}
            icon={<ShoppingBag className="size-6" />}
            title={t("mainMenu.online")}
            description={t("mainMenu.onlineDesc")}
            highlight
          />
        ) : null}
      </div>
      {!dineIn && !online ? (
        <EmptyState
          title={t("mainMenu.unavailable")}
          description={t("mainMenu.unavailableDesc")}
        />
      ) : null}
    </PageShell>
  );
}

function OptionCard({
  to,
  image,
  icon,
  title,
  description,
  highlight,
}: {
  to: "/branchMenu" | "/menu" | "/order-type";
  image: ResponsiveImageSource;
  icon: React.ReactNode;
  title: string;
  description: string;
  highlight?: boolean | undefined;
}) {
  return (
    <Link
      to={to}
      className={`pressable surface-card block overflow-hidden hover:shadow-lift ${
        highlight ? "ring-2 ring-primary/25" : ""
      }`}
    >
      <div className="relative h-60 overflow-hidden">
        <ResponsiveImage
          source={image}
          sizes="(min-width: 768px) 50vw, 100vw"
          alt=""
          loading="lazy"
          className="size-full object-cover"
        />
        <div className="hero-scrim absolute inset-0 opacity-70" />
        <span className="absolute bottom-4 start-4 grid size-12 place-items-center rounded-2xl bg-card text-primary shadow-soft">
          {icon}
        </span>
      </div>
      <div className="p-6">
        <h2 className="text-2xl font-bold">{title}</h2>
        <p className="mt-2 leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
    </Link>
  );
}
